/*
  Sent.js - Component to handle Sent SMS content
*/

// NPM
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Link,
  Stack,
  Typography,
  TableContainer,
  Table,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  Grid,
  Checkbox,
} from '@mui/material';

// COMPONENTS
import SentScheduledGroup from '../../components/websms/scheduled/SentScheduledGroup';
import Paginator from '../../components/common/Paginator';
import DateRangeSelector from '../../components/common/DateRangeSelector';
import SectionWrapper from '../../components/common/SectionWrapper';
import ConfirmationDialog from '../../components/websms/sent/ConfirmationDialog';
import AdvancedFiltersContainer from '../../components/common/AdvancedFiltersContainer';
import DataExportModal from '../../components/common/modals/DataExportModal';
import HelpTooltip from '../../components/common/HelpTooltip';

// LAYOUT
import Layout from '../../components/layouts/Layout';

// ROUTES
import { WebsmsRoutes } from '../../routes/WebsmsRoutes';

// MESSAGES
import ScheduledFormattedMessages from '../../messages/websms/ScheduledFormattedMessages';

// CONFIG
import config from '../../Config';

// NETWORK
import useScheduledRequest from '../../requests/ScheduledRequest';

// UTILS
import { getZoneOffsettedDate } from '../../utils/dates';

const Scheduled = ({ errorHandler }) => {
  const [scheduledItems, setScheduledItems] = useState([]);
  const [messageSelected, setMessageSelected] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [subjectFilter, setSubjectFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [messageFilter, setMessageFilter] = useState('');
  const [timeRange, setTimeRange] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(config.defaultPL);
  const [currentlySearching, setCurrentlySearching] = useState({});
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectorKeyRefresh, setSelectorKeyRefresh] = useState(false);
  const [cancelList, setCancelList] = useState(new Set());
  const [filteredText, setFilteredText] = useState([]);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [showDataExportModal, setShowDataExportModal] = useState(false);

  const {
    searchScheduledItems,
    cancelMessagesByGroup,
    cancelAllPendingMessages,
  } = useScheduledRequest();

  const sendStatus = [
    'Blacklisted',
    'Cancelled',
    'Delivered',
    'Failed Delivery',
    'Invalid Number',
    'Pending',
    'Presumed Delivered',
    'Processing',
    'Too Many Retries',
  ];

  const handleBackButton = () => {
    setMessageSelected(false);
    setMessage(null);
    setCancelList(new Set());
  };

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
  };

  const handleUserDataSearch = async (fromDate, toDate, timeRange) => {
    try {
      setIsSearching(true);
      setScheduledItems([]);

      const params = {
        fromDate,
        toDate,
        timeRangeStart: fromDate,
        timeRangeEnd: toDate,
        subjectFilter,
        toFilter,
        messageFilter,
        type: 'TXT',
        timeRange,
        pageNumber,
        pageLength,
      };
      setSearchParams(params);

      const response = await searchScheduledItems(params, errorHandler);
      console.log('scheduled items:', response);

      setScheduledItems(response.items);
      setTimeRange(timeRange);
      setCurrentlySearching(params);

      setRowCount(response.rowCount);
      setMessageCount(response.messageCount);
      setPendingCount(response.pendingCount);
      setPageTotal(response.pageTotal);
      setIsSearching(false);
      setCancelList(new Set());
      generateSearchText();
    } catch (e) {
      console.error(e);
      setIsSearching(false);
      setCancelList(new Set());
      setError(e.message);
    }
  };

  // const exportSentMessages = async () => {
  //   const response = await requestExportSentMessages(
  //     currentlySearching,
  //     errorHandler
  //   );
  //   console.log('response:', response);
  //   setEmailRecipient(response);
  //   setShowDataExportModal(true);
  // };

  const handleCancelAllPendingMessages = async () => {
    setOpenConfirmationDialog(false);
    try {
      if (cancelList.size > 0) {
        await cancelMessagesByGroup(Array.from(cancelList), errorHandler);
      } else {
        await cancelAllPendingMessages();
      }
      setSelectorKeyRefresh(!selectorKeyRefresh);
      setCancelList(new Set());
      handleUserDataSearch(fromDate, toDate);
    } catch (e) {
      errorHandler(e.message);
    }
  };

  const handleShowConfirmation = () => {
    setOpenConfirmationDialog(!openConfirmationDialog);
  };

  const handleCancelList = (emailId, remove) => {
    let newCancelList;

    if (emailId === 'visible') {
      newCancelList = remove
        ? new Set()
        : new Set(
            scheduledItems
              .filter((item) => item.total_messages > 0)
              .map((item) => item.email_id)
          );
    } else {
      newCancelList = new Set(cancelList);
      if (remove) newCancelList.delete(emailId);
      else newCancelList.add(emailId);
    }

    console.log('cancelList:', newCancelList);
    setCancelList(newCancelList);
  };

  const generateSearchText = () => {
    if (message) return null;

    const filterText = [];
    const filterValues = ['FROM:', 'TO:', 'NAME:', 'MESSAGE:', 'STATUS:'];
    [subjectFilter, toFilter, messageFilter].forEach((filter, i) => {
      if (filter !== '')
        filterText.push(
          <span key={i}>
            {filterText && filterText.length === 0 ? '' : ', '}
            <strong>{filterValues[i]}</strong> {filter}
          </span>
        );
    });

    setFilteredText(filterText);
  };

  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
    setCancelList(new Set());
  };

  const handleCheckboxChange = (event, id) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    handleCancelList(id, !value);
  };

  const changePageEvent = (page) => {
    setPageNumber(page);
    setCancelList(new Set());
  };

  const handlePageReset = () => {
    setMessage(null);
    setScheduledItems([]);
    setSelectorKeyRefresh(!selectorKeyRefresh);
  };

  const clearFilters = () => {
    setSubjectFilter('');
    setToFilter('');
    setMessageFilter('');
    document.getElementById('filter-form').reset();
    setFilteredText('');
    setPageNumber(1);
    handlePageReset();
  };

  useEffect(() => {
    if (fromDate && toDate) handleUserDataSearch(fromDate, toDate, timeRange);
  }, [fromDate, toDate, pageNumber, pageLength]);

  const generateExportButton = () => {
    // let message = 'Export';
    // return (
    //   <Box>
    //     <Button
    //       className="maxHeight float-right"
    //       variant="outlined"
    //       color="secondary"
    //       type="submit"
    //       onClick={exportSentMessages}
    //       sx={{ backgroundColor: 'common.white' }}>
    //       {message}
    //     </Button>
    //   </Box>
    // );
  };

  const generateCancelPendingButton = () => {
    let message = ScheduledFormattedMessages.cancelAllPendingMessages;
    if (cancelList.size > 0)
      message = ScheduledFormattedMessages.cancelSelectedPendingMessages;
    return (
      <Box>
        <Button
          className="maxHeight float-right"
          variant="outlined"
          color="secondary"
          type="submit"
          onClick={handleShowConfirmation}
          sx={{ backgroundColor: 'common.white' }}
        >
          {message}
        </Button>
      </Box>
    );
  };

  const showConfirmationDialog = () => {
    let modalMessage = ScheduledFormattedMessages.cancelConfirmationMessage;
    if (cancelList.size > 0)
      modalMessage = `Are you sure you would like to cancel ${cancelList.size} message(s)`;

    return (
      <ConfirmationDialog
        showConfirmation={openConfirmationDialog}
        setShowConfirmation={setOpenConfirmationDialog}
        handleCloseConfirmation={handleShowConfirmation}
        message={modalMessage}
        handleCancelPendingMessage={handleCancelAllPendingMessages}
      />
    );
  };

  const renderScheduledTable = () => {
    if (isSearching) {
      return (
        <TableRow>
          <TableCell colSpan={7} sx={{ paddingY: 2 }}>
            <Typography>{ScheduledFormattedMessages.searchingText}</Typography>
          </TableCell>
        </TableRow>
      );
    } else if (!scheduledItems || !scheduledItems.length) {
      return (
        <TableRow>
          <TableCell colSpan={7} sx={{ paddingY: 2 }}>
            <Typography>{ScheduledFormattedMessages.noResult}</Typography>
          </TableCell>
        </TableRow>
      );
    } else {
      return scheduledItems.map((messages, index) => (
        <TableRow key={index}>
          <TableCell>{getZoneOffsettedDate(messages.sms_sent)}</TableCell>
          <TableCell>{messages.group_subject || 'N/A'}</TableCell>
          <TableCell>{messages.total_messages || 'N/A'}</TableCell>
          <TableCell>{messages.dest_tn || '-'}</TableCell>
          <TableCell>
            <Link
              onClick={() => {
                setMessageSelected(true);
                setMessage(messages);
              }}
              tabIndex={0}
            >
              {decodeURIComponent(messages.parent_sms_text)}
            </Link>
          </TableCell>
          <TableCell>
            <Checkbox
              value={messages.sms_id}
              onChange={(event) =>
                handleCheckboxChange(event, messages.email_id)
              }
              checked={cancelList.has(messages.email_id)}
              disabled={messages.total_messages === 0}
              sx={{ padding: 0 }}
            />
          </TableCell>
        </TableRow>
      ));
    }
  };

  return (
    <Layout
      routes={WebsmsRoutes}
      title={ScheduledFormattedMessages.scheduledHeader}
    >
      {messageSelected ? (
        <SentScheduledGroup
          groupId={message.email_id}
          handleBackButton={handleBackButton}
          errorHandler={errorHandler}
        />
      ) : (
        <Grid container sx={{ justifyContent: 'space-between', rowGap: 2 }}>
          <Grid size={{ xs: 12, lg: 9 }}>
            <SectionWrapper
              title={ScheduledFormattedMessages.searchScheduledMessages}
              icon="search"
              sx={{ maxWidth: 1000 }}
            >
              <DateRangeSelector
                key={selectorKeyRefresh ? 1 : 0}
                handleDateSearch={handleUserDataSearch}
                parentStartDate={fromDate}
                setParentStartDate={setFromDate}
                parentEndDate={toDate}
                setParentEndDate={setToDate}
                scheduledDates={true}
              />

              <AdvancedFiltersContainer>
                <form id="filter-form">
                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      marginY: 2,
                      maxWidth: { lg: 674 },
                    }}
                  >
                    <Stack>
                      <Typography sx={{ whiteSpace: 'nowrap', marginTop: 1 }}>
                        {ScheduledFormattedMessages.byField}
                      </Typography>
                    </Stack>

                    <Stack sx={{ gap: 1, flex: 1 }}>
                      <Stack
                        sx={{
                          flexDirection: { lg: 'row' },
                          gap: 2,
                          alignItems: { lg: 'center' },
                        }}
                      >
                        <TextField
                          label={ScheduledFormattedMessages.subject}
                          value={subjectFilter}
                          onChange={handleInputChange(setSubjectFilter)}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label={ScheduledFormattedMessages.to}
                          value={toFilter}
                          onChange={handleInputChange(setToFilter)}
                          size="small"
                          fullWidth
                        />
                      </Stack>

                      <Typography fontSize={12} sx={{ color: 'grey.600' }}>
                        {ScheduledFormattedMessages.filterNote}
                      </Typography>

                      <Stack>
                        <Stack
                          sx={{
                            flexDirection: { lg: 'row' },
                            gap: 2,
                            alignItems: { lg: 'center' },
                          }}
                        >
                          <TextField
                            label={ScheduledFormattedMessages.message}
                            value={messageFilter}
                            onChange={handleInputChange(setMessageFilter)}
                            size="small"
                            fullWidth
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </form>
              </AdvancedFiltersContainer>

              <Stack
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {filteredText && filteredText.length !== 0 && (
                  <Button
                    color="secondary"
                    onClick={clearFilters}
                    sx={{
                      textTransform: 'none',
                      textDecoration: 'underline',
                      fontWeight: 400,
                    }}
                  >
                    {ScheduledFormattedMessages.clearFilters}
                  </Button>
                )}

                <Button
                  onClick={() => {
                    setPageNumber(1);
                    handlePageReset();
                  }}
                  color="secondary"
                  variant="outlined"
                >
                  {ScheduledFormattedMessages.searchButton}
                </Button>
              </Stack>
            </SectionWrapper>
          </Grid>

          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent:
                  filteredText && filteredText.length !== 0
                    ? 'space-between'
                    : 'flex-end',
                alignItems: 'flex-end',
                marginBottom: 1,
              }}
            >
              {filteredText && filteredText.length !== 0 && (
                <Typography fontSize={12}>{filteredText}</Typography>
              )}
              <Stack direction="row" spacing={2}>
                {generateExportButton()}
                {generateCancelPendingButton()}
              </Stack>
            </Stack>
            <TableContainer
              sx={{
                marginBottom: 4,
                overflowX: 'auto',
                width: '100%',
              }}
            >
              <Table
                variant="rounded"
                sx={{
                  minWidth: 1000,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell width="15%">
                      {ScheduledFormattedMessages.dateTimeText}
                    </TableCell>
                    <TableCell width="15%">
                      {ScheduledFormattedMessages.subjectHeader}
                    </TableCell>
                    <TableCell width="15%">
                      {ScheduledFormattedMessages.recipientCountHeader}
                    </TableCell>
                    <TableCell width="15%">
                      {ScheduledFormattedMessages.toHeader}
                    </TableCell>
                    <TableCell width="20%">
                      {ScheduledFormattedMessages.messageText}
                    </TableCell>
                    <TableCell width="5%" sx={{ color: 'grey.700' }}>
                      <Checkbox
                        value={'visible'}
                        onChange={(event) =>
                          handleCheckboxChange(event, 'visible')
                        }
                        checked={pageTotal && cancelList.size === pageTotal}
                        sx={{ padding: 0 }}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{renderScheduledTable()}</TableBody>
              </Table>
            </TableContainer>
            <Stack direction="row" alignItems="center" gap={2}>
              <Paginator
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                itemCount={rowCount ?? 0}
                changeEvent={changePageEvent}
                pageLength={pageLength}
                editFunction={(e) => changePageLength(e)}
                showTotal={ScheduledFormattedMessages.found}
                toolTip={ScheduledFormattedMessages.foundTooltip}
              />
              <Stack
                sx={{ flexDirection: 'row', gap: 0.5, alignItems: 'center' }}
              >
                <Typography fontSize={12}>Message: {messageCount}</Typography>
                <HelpTooltip
                  message={ScheduledFormattedMessages.messageCountTooltip}
                />
              </Stack>
              <Stack
                sx={{ flexDirection: 'row', gap: 0.5, alignItems: 'center' }}
              >
                <Typography fontSize={12}>Pending: {pendingCount}</Typography>
                <HelpTooltip
                  message={ScheduledFormattedMessages.pendingCountTooltip}
                />
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      )}
      {showConfirmationDialog()}
      <DataExportModal
        show={showDataExportModal}
        onHide={() => setShowDataExportModal(false)}
        emailRecipient={emailRecipient}
      />
    </Layout>
  );
};

export default Scheduled;
